import { lazy, Suspense, useEffect } from "react";
import { Link, Route, Routes } from "react-router-dom";

import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";
import { ConfigProvider, useConfig } from "@/config/ConfigProvider";
import { ThemeProvider, ThemeStyle } from "@/config/theme";
import { initSentry } from "@/lib/sentry";
import ScrollToTop from "./components/ScrollToTop";

const HomePage = lazy(() => import("@/pages/index"));
const ServicesPage = lazy(() => import("@/pages/services"));
const PricingPage = lazy(() => import("@/pages/pricing"));
const AboutPage = lazy(() => import("@/pages/about"));
const ContactPage = lazy(() => import("@/pages/contact"));

function NotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="font-heading text-7xl font-bold">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm opacity-70">
          The page you&apos;re looking for doesn&apos;t exist or has been moved.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center rounded-full bg-[var(--accent)] px-5 py-3 text-sm font-semibold text-[var(--accent-foreground)]"
        >
          Go home
        </Link>
      </div>
    </div>
  );
}

function SiteShell() {
  const config = useConfig();

  useEffect(() => {
    void initSentry(config);
  }, [config]);

  return (
    <>
      <ThemeStyle />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-[var(--accent)] focus:px-4 focus:py-2 focus:text-[var(--accent-foreground)]"
      >
        Skip to content
      </a>
      <Header />
      <main id="main" className="min-h-screen font-body">
        <Suspense fallback={<div className="min-h-screen" />}>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <ConfigProvider>
      <ThemeProvider>
        <SiteShell />
      </ThemeProvider>
    </ConfigProvider>
  );
}
